import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Gift, Copy, Check, Users, Coins, Clock, TrendingUp, Share2, QrCode, Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper function to calculate days remaining
const calculateDaysRemaining = (trialEndsAt) => {
  if (!trialEndsAt) return null;
  const endDate = new Date(trialEndsAt);
  const now = new Date();
  const diffTime = endDate - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 0;
};

// Helper function to format date in DD.MM.YYYY
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

export const ReferralDashboard = () => {
  const { user } = useAuth();
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/referral/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralData(response.data);
    } catch (error) {
      console.error('Error fetching referral data:', error);
      toast.error('Fehler beim Laden der Referral-Daten');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        toast.success('Link kopiert!');
        setTimeout(() => setCopied(false), 2000);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          textArea.remove();
          setCopied(true);
          toast.success('Link kopiert!');
          setTimeout(() => setCopied(false), 2000);
        } catch (err) {
          textArea.remove();
          toast.error('Bitte Link manuell kopieren.');
        }
      }
    } catch (err) {
      toast.error('Bitte Link manuell kopieren.');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  const referralLink = referralData?.referral_link || '';
  const referralCode = referralData?.referral_code || 'LOADING';

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Gift className="h-7 w-7 sm:h-8 sm:w-8 text-primary" />
            Freunde einladen
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Werbe Freunde und erhalte 40 € Rabatt (inkl. USt) auf deine nächste Rechnung pro qualifiziertem Neukunden! 🎉
          </p>
        </div>

        {/* Main Card - Referral Link */}
        <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
              <Share2 className="h-5 w-5" />
              Dein Einladungs-Link
            </CardTitle>
            <CardDescription className="text-sm">
              Teile diesen Link mit Freunden und Geschäftspartnern
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Referral Code */}
            <div className="flex items-center gap-2 p-3 bg-background rounded-lg border">
              <div className="flex-1">
                <p className="text-xs text-muted-foreground mb-1">Dein Code:</p>
                <p className="text-lg sm:text-xl font-bold font-mono">{referralCode}</p>
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => copyToClipboard(referralCode)}
                className="shrink-0"
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>

            {/* Referral Link */}
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                value={referralLink}
                readOnly
                className="flex-1 font-mono text-xs sm:text-sm"
              />
              <Button
                onClick={() => copyToClipboard(referralLink)}
                className="w-full sm:w-auto"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Kopiert!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Link kopieren
                  </>
                )}
              </Button>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-blue-900 dark:text-blue-100 font-semibold mb-3">
                So einfach profitierst du und deine Partner:
              </p>
              <ol className="text-xs sm:text-sm text-blue-800 dark:text-blue-200 space-y-2 list-decimal list-inside">
                <li><strong>Link teilen:</strong> Sende deinen persönlichen Empfehlungslink an Freunde oder Geschäftspartner.</li>
                <li><strong>Exklusiver Start-Vorteil:</strong> Neue Partner erhalten durch deinen Link exklusive Einführungskonditionen.</li>
                <li><strong>Deine Belohnung:</strong> Sobald dein geworbener Neukunde nach seiner Gratisphase erstmals tatsächlich bezahlt, erhältst du 40,00 € Rabatt (inkl. USt) auf deine nächste Rechnung.</li>
                <li><strong>Automatische Verrechnung:</strong> Der Rabatt senkt den Preis deiner nächsten Rechnung; die Umsatzsteuer wird auf dem reduzierten Betrag neu berechnet. Ein Restbetrag wird auf Folgerechnungen übertragen.</li>
              </ol>
            </div>
          </CardContent>
        </Card>

        {/* Statistics Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center text-center space-y-2">
                <div className="p-3 bg-primary/10 rounded-full">
                  <Users className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                </div>
                <div>
                  <p className="text-2xl sm:text-3xl font-bold">{referralData?.total_referrals || 0}</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">Einladungen</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center text-center space-y-2">
                <div className="p-3 bg-orange-100 dark:bg-orange-900/20 rounded-full">
                  <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <p className="text-2xl sm:text-3xl font-bold">{referralData?.pending_referrals || 0}</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">Wartend</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center text-center space-y-2">
                <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-full">
                  <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-2xl sm:text-3xl font-bold">{referralData?.completed_referrals || 0}</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">Erfolgreich</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center text-center space-y-2">
                <div className="p-3 bg-emerald-100 dark:bg-emerald-900/20 rounded-full">
                  <Coins className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl sm:text-3xl font-bold" data-testid="reward-available-net">{referralData?.reward_available_gross_eur?.toFixed(2) || '0.00'}€</p>
                  <p className="text-xs sm:text-sm text-muted-foreground">Verfügbarer Rabatt (inkl. USt)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Referral List */}
        {referralData?.referrals && referralData.referrals.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg sm:text-xl">Deine Einladungen</CardTitle>
              <CardDescription>Übersicht aller eingeladenen Freunde</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {referralData.referrals.map((referral) => {
                  const daysRemaining = calculateDaysRemaining(referral.trial_ends_at);
                  const isExpiringSoon = daysRemaining !== null && daysRemaining <= 7;
                  
                  return (
                    <div
                      key={referral.id}
                      className="flex items-center justify-between p-3 sm:p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm sm:text-base truncate">
                          Kunde #{referral.referred_id.slice(0, 8)}
                        </p>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          {new Date(referral.created_at).toLocaleDateString('de-DE')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {['completed','reward_available','partially_applied','fully_applied'].includes(referral.status) ? (
                          <div className="flex flex-col items-end gap-1" data-testid={`referral-reward-${referral.id}`}>
                            <div className="flex items-center gap-2">
                              <span className="text-green-600 dark:text-green-400 font-semibold text-sm sm:text-base">
                                40 € Rabatt
                              </span>
                              <span className="px-2 py-1 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 text-xs rounded-full">
                                {referral.status === 'fully_applied' ? '✓ Vollständig verrechnet' : referral.status === 'partially_applied' ? '◑ Teilweise verrechnet' : '✓ Rabatt verfügbar'}
                              </span>
                            </div>
                            {typeof referral.reward_remaining_gross_eur === 'number' && (referral.status === 'partially_applied' || referral.status === 'reward_available') && referral.reward_remaining_gross_eur > 0 && (
                              <span className="text-xs text-muted-foreground" data-testid={`referral-remaining-${referral.id}`}>
                                Noch {referral.reward_remaining_gross_eur.toLocaleString('de-DE', { minimumFractionDigits: 2 })} € offen (inkl. USt)
                              </span>
                            )}
                            {typeof referral.reward_consumed_gross_eur === 'number' && referral.reward_consumed_gross_eur > 0 && (
                              <span className="text-xs text-muted-foreground" data-testid={`referral-consumed-${referral.id}`}>
                                {referral.reward_consumed_gross_eur.toLocaleString('de-DE', { minimumFractionDigits: 2 })} € bereits verrechnet
                              </span>
                            )}
                          </div>
                        ) : ['pending','awaiting_first_payment'].includes(referral.status) ? (
                          <div className="flex flex-col items-end gap-1">
                            <div className="flex items-center gap-1">
                              <span className="px-2 py-1 bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 text-xs rounded-full">
                                ⏳ Wartet auf erste Zahlung
                              </span>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    <p className="text-xs">
                                      Sobald der geworbene Neukunde nach seiner Gratisphase erstmals tatsächlich bezahlt, erhalten Sie 40,00 € Rabatt (inkl. USt) auf Ihre nächste Rechnung.
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                            {daysRemaining !== null && (
                              <span className={`text-xs ${isExpiringSoon ? 'text-yellow-600 dark:text-yellow-400 font-semibold' : 'text-muted-foreground'}`}>
                                ⏱️ Noch {daysRemaining} {daysRemaining === 1 ? 'Tag' : 'Tage'} (bis {formatDate(referral.trial_ends_at)})
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs rounded-full">
                            Storniert
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {(!referralData?.referrals || referralData.referrals.length === 0) && (
          <Card>
            <CardContent className="py-12">
              <div className="text-center space-y-3">
                <div className="flex justify-center">
                  <div className="p-4 bg-primary/10 rounded-full">
                    <Users className="h-10 w-10 text-primary" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold">Noch keine Einladungen</h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  Teile deinen Einladungs-Link mit Freunden und erhalte 40 € Rabatt (inkl. USt) pro qualifiziertem Neukunden!
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ReferralDashboard;
